import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { availableAtLocation, lineRequiresBackOrder } from '@/lib/orderAvailability';
import type { CartLine, InventoryRecord, OrderCustomer, OrderItem } from '@/types/domain';

interface CustomerCart {
  customer: OrderCustomer;
  lines: CartLine[];
  mixingInstructions: string;
}

interface AddOptions {
  unitPrice?: number;
  location?: string;
  availableAtLocation?: number;
  isBackOrder?: boolean;
  mixInstructions?: string;
  overrideReason?: string;
}

interface CartContextValue {
  customer: OrderCustomer | null;
  lines: CartLine[];
  cartCount: number;
  subtotal: number;
  mixingInstructions: string;
  selectCustomer: (customer: OrderCustomer) => void;
  addItem: (item: OrderItem, quantity: number, options?: AddOptions) => void;
  applyDispatchLocation: (
    locationCode: string,
    inventory: InventoryRecord[],
  ) => void;
  updateQuantity: (itemNo: string, quantity: number) => void;
  removeItem: (itemNo: string) => void;
  toggleItemMixing: (itemNo: string, enabled: boolean) => void;
  updateItemMixingInstructions: (itemNo: string, value: string) => void;
  setMixingInstructions: (value: string) => void;
  clearCurrentCart: () => void;
  clearAllCarts: () => void;
}

const STORAGE_KEY = 'carrfields-order-carts';
const CartContext = createContext<CartContextValue | null>(null);

function unitPriceForMixing(item: OrderItem, baseUnitPrice: number, enabled: boolean): number {
  const unit = item.base_unit_of_measure?.toUpperCase() || '';
  const canReduce = (unit === 'KG' || unit === 'TONNE') && Boolean(item.include_mixing_fee_in_price);
  return !enabled && canReduce ? Math.max(0, baseUnitPrice - 0.2) : baseUnitPrice;
}

function loadCarts(): Record<string, CustomerCart> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') as Record<string, CustomerCart>;
  } catch {
    return {};
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [carts, setCarts] = useState<Record<string, CustomerCart>>(loadCarts);
  const [customerId, setCustomerId] = useState('');

  const persist = useCallback((next: Record<string, CustomerCart>) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    return next;
  }, []);

  const updateCurrent = useCallback((updater: (cart: CustomerCart) => CustomerCart) => {
    if (!customerId) return;
    setCarts((current) => {
      const existing = current[customerId];
      if (!existing) return current;
      return persist({ ...current, [customerId]: updater(existing) });
    });
  }, [customerId, persist]);

  const selectCustomer = useCallback((customer: OrderCustomer) => {
    setCustomerId(customer.customer_no);
    setCarts((current) => current[customer.customer_no]
      ? current
      : persist({
          ...current,
          [customer.customer_no]: { customer, lines: [], mixingInstructions: '' },
        }));
  }, [persist]);

  const addItem = useCallback((item: OrderItem, quantity: number, options: AddOptions = {}) => {
    if (!customerId) throw new Error('Select a customer before adding products.');
    if (!item.is_third_party && !options.location && !options.isBackOrder) {
      throw new Error('Select a fulfilment location or mark this item as a back order.');
    }
    updateCurrent((cart) => {
      const existing = cart.lines.find((line) => line.item_no === item.item_no);
      const baseUnitPrice = options.unitPrice ?? existing?.baseUnitPrice ?? item.unit_price ?? 0;
      const requiresMixing = existing?.requiresMixing ?? false;
      const unitPrice = unitPriceForMixing(item, baseUnitPrice, requiresMixing);
      const nextLine: CartLine = {
        ...item,
        qty: (existing?.qty || 0) + quantity,
        unitPrice,
        baseUnitPrice,
        location: options.location || existing?.location,
        availableAtLocation:
          options.availableAtLocation ?? existing?.availableAtLocation,
        isBackOrder: options.isBackOrder ?? existing?.isBackOrder,
        mixInstructions: options.mixInstructions || existing?.mixInstructions,
        overrideReason: options.overrideReason || existing?.overrideReason,
        requiresMixing,
      };
      return {
        ...cart,
        lines: existing
          ? cart.lines.map((line) => line.item_no === item.item_no ? nextLine : line)
          : [...cart.lines, nextLine],
      };
    });
  }, [customerId, updateCurrent]);

  const updateQuantity = useCallback((itemNo: string, quantity: number) => {
    updateCurrent((cart) => ({
      ...cart,
      lines: quantity <= 0
        ? cart.lines.filter((line) => line.item_no !== itemNo)
        : cart.lines.map((line) => {
            if (line.item_no !== itemNo) return line;
            return {
              ...line,
              qty: quantity,
              isBackOrder:
                line.availableAtLocation === undefined
                  ? line.isBackOrder
                  : lineRequiresBackOrder(
                      { ...line, qty: quantity },
                      line.availableAtLocation,
                    ),
            };
          }),
    }));
  }, [updateCurrent]);

  const applyDispatchLocation = useCallback((
    locationCode: string,
    inventory: InventoryRecord[],
  ) => {
    updateCurrent((cart) => ({
      ...cart,
      lines: cart.lines.map((line) => {
        const available = availableAtLocation(
          inventory,
          line.item_no,
          locationCode,
        );
        return {
          ...line,
          location: locationCode,
          availableAtLocation: line.is_third_party ? undefined : available,
          isBackOrder: lineRequiresBackOrder(line, available),
        };
      }),
    }));
  }, [updateCurrent]);

  const removeItem = useCallback((itemNo: string) => {
    updateCurrent((cart) => ({ ...cart, lines: cart.lines.filter((line) => line.item_no !== itemNo) }));
  }, [updateCurrent]);

  const toggleItemMixing = useCallback((itemNo: string, enabled: boolean) => {
    updateCurrent((cart) => ({
      ...cart,
      lines: cart.lines.map((line) => {
        if (line.item_no !== itemNo) return line;
        const baseUnitPrice = line.baseUnitPrice ?? line.unitPrice;
        return {
          ...line,
          baseUnitPrice,
          requiresMixing: enabled,
          unitPrice: unitPriceForMixing(line, baseUnitPrice, enabled),
        };
      }),
    }));
  }, [updateCurrent]);

  const updateItemMixingInstructions = useCallback((itemNo: string, mixInstructions: string) => {
    updateCurrent((cart) => ({
      ...cart,
      lines: cart.lines.map((line) => line.item_no === itemNo ? { ...line, mixInstructions } : line),
    }));
  }, [updateCurrent]);

  const setMixingInstructions = useCallback((mixingInstructions: string) => {
    updateCurrent((cart) => ({ ...cart, mixingInstructions }));
  }, [updateCurrent]);

  const clearCurrentCart = useCallback(() => {
    if (!customerId) return;
    setCarts((current) => {
      const next = { ...current };
      delete next[customerId];
      return persist(next);
    });
    setCustomerId('');
  }, [customerId, persist]);

  const clearAllCarts = useCallback(() => {
    setCarts(persist({}));
    setCustomerId('');
  }, [persist]);

  const cart = carts[customerId];
  const lines = useMemo(() => cart?.lines || [], [cart]);
  const value = useMemo<CartContextValue>(() => ({
    customer: cart?.customer || null,
    lines,
    cartCount: lines.reduce((count, line) => count + line.qty, 0),
    subtotal: lines.reduce((total, line) => total + line.qty * line.unitPrice, 0),
    mixingInstructions: cart?.mixingInstructions || '',
    selectCustomer,
    addItem,
    applyDispatchLocation,
    updateQuantity,
    removeItem,
    toggleItemMixing,
    updateItemMixingInstructions,
    setMixingInstructions,
    clearCurrentCart,
    clearAllCarts,
  }), [addItem, applyDispatchLocation, cart, clearAllCarts, clearCurrentCart, lines, removeItem, selectCustomer, setMixingInstructions, toggleItemMixing, updateItemMixingInstructions, updateQuantity]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const value = useContext(CartContext);
  if (!value) throw new Error('useCart must be used inside CartProvider.');
  return value;
}
